// Minimal streaming ZIP writer (STORE / no compression) + save helpers.
//
// Why hand-rolled: project rules forbid heavy deps, and we want to STREAM each file
// straight to disk (via the Save-File-Picker's writable stream) so large videos never
// sit in memory. Each entry uses a data descriptor (general-purpose bit 3) so we can
// write the local header before knowing the file's size/CRC, then stream the bytes and
// emit CRC/size afterwards. UTF-8 filenames (bit 11). No ZIP64 — individual files and the
// whole archive must each stay under 4 GB (fine for a gallery selection; noted to callers).

export interface ZipEntry {
  name: string;
  /** Lazily open the file's byte stream (called when this entry's turn comes). */
  stream: () => Promise<ReadableStream<Uint8Array>>;
}

export type ZipProgress = (entryIndex: number, name: string, phase: 'start' | 'done') => void;

// ---- CRC32 (incremental) ----
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
/** Fold `data` into a running CRC register (init 0xFFFFFFFF; finalize with ^0xFFFFFFFF). */
function crcUpdate(reg: number, data: Uint8Array): number {
  let c = reg;
  for (let i = 0; i < data.length; i++) c = (c >>> 8) ^ CRC_TABLE[(c ^ data[i]) & 0xff];
  return c >>> 0;
}

function dosDateTime(d: Date): { time: number; date: number } {
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return { time: time & 0xffff, date: date & 0xffff };
}

function buf(len: number): { b: Uint8Array; v: DataView } {
  const b = new Uint8Array(len);
  return { b, v: new DataView(b.buffer) };
}

function localHeader(name: Uint8Array, time: number, date: number): Uint8Array {
  const { b, v } = buf(30 + name.length);
  v.setUint32(0, 0x04034b50, true);
  v.setUint16(4, 20, true);     // version needed
  v.setUint16(6, 0x0808, true); // flags: data descriptor (bit 3) + UTF-8 (bit 11)
  v.setUint16(8, 0, true);      // method: store
  v.setUint16(10, time, true);
  v.setUint16(12, date, true);
  v.setUint32(14, 0, true);     // crc (in data descriptor)
  v.setUint32(18, 0, true);     // compressed size (in data descriptor)
  v.setUint32(22, 0, true);     // uncompressed size (in data descriptor)
  v.setUint16(26, name.length, true);
  v.setUint16(28, 0, true);     // extra length
  b.set(name, 30);
  return b;
}

function dataDescriptor(crc: number, size: number): Uint8Array {
  const { b, v } = buf(16);
  v.setUint32(0, 0x08074b50, true);
  v.setUint32(4, crc, true);
  v.setUint32(8, size >>> 0, true);  // compressed size (== uncompressed; store)
  v.setUint32(12, size >>> 0, true); // uncompressed size
  return b;
}

function centralHeader(name: Uint8Array, time: number, date: number, crc: number, size: number, offset: number): Uint8Array {
  const { b, v } = buf(46 + name.length);
  v.setUint32(0, 0x02014b50, true);
  v.setUint16(4, 20, true);     // version made by
  v.setUint16(6, 20, true);     // version needed
  v.setUint16(8, 0x0808, true); // flags
  v.setUint16(10, 0, true);     // method
  v.setUint16(12, time, true);
  v.setUint16(14, date, true);
  v.setUint32(16, crc, true);
  v.setUint32(20, size >>> 0, true);
  v.setUint32(24, size >>> 0, true);
  v.setUint16(28, name.length, true);
  v.setUint16(30, 0, true);     // extra length
  v.setUint16(32, 0, true);     // comment length
  v.setUint16(34, 0, true);     // disk number start
  v.setUint16(36, 0, true);     // internal attrs
  v.setUint32(38, 0, true);     // external attrs
  v.setUint32(42, offset >>> 0, true); // local header offset
  b.set(name, 46);
  return b;
}

function eocd(count: number, cdSize: number, cdOffset: number): Uint8Array {
  const { b, v } = buf(22);
  v.setUint32(0, 0x06054b50, true);
  v.setUint16(4, 0, true);          // disk number
  v.setUint16(6, 0, true);          // disk with central dir
  v.setUint16(8, count & 0xffff, true);
  v.setUint16(10, count & 0xffff, true);
  v.setUint32(12, cdSize >>> 0, true);
  v.setUint32(16, cdOffset >>> 0, true);
  v.setUint16(20, 0, true);         // comment length
  return b;
}

/** Stream a ZIP of `entries` into `sink`, closing it when done. */
export async function writeZip(sink: WritableStream<Uint8Array>, entries: ZipEntry[], onProgress?: ZipProgress): Promise<void> {
  const writer = sink.getWriter();
  const enc = new TextEncoder();
  const { time, date } = dosDateTime(new Date());
  const central: Uint8Array[] = [];
  let offset = 0;
  const write = async (b: Uint8Array) => { await writer.write(b); offset += b.length; };
  try {
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const nameBytes = enc.encode(entry.name);
      const localOffset = offset;
      onProgress?.(i, entry.name, 'start');
      await write(localHeader(nameBytes, time, date));
      let reg = 0xffffffff;
      let size = 0;
      const reader = (await entry.stream()).getReader();
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          reg = crcUpdate(reg, value);
          size += value.length;
          await write(value);
        }
      } finally {
        reader.releaseLock();
      }
      const crc = (reg ^ 0xffffffff) >>> 0;
      await write(dataDescriptor(crc, size));
      central.push(centralHeader(nameBytes, time, date, crc, size, localOffset));
      onProgress?.(i, entry.name, 'done');
    }
    const cdStart = offset;
    let cdSize = 0;
    for (const c of central) { await write(c); cdSize += c.length; }
    await write(eocd(central.length, cdSize, cdStart));
    await writer.close();
  } catch (e) {
    try { await writer.abort(e); } catch { /* ignore */ }
    throw e;
  }
}

// ---- filename hygiene ----
function cleanName(raw: string): string {
  return (raw || 'file').replace(/[\\/:*?"<>|\x00-\x1f]/g, '_').replace(/^\.+/, '').trim() || 'file';
}
function dedupe(entries: ZipEntry[]): ZipEntry[] {
  const used = new Set<string>();
  return entries.map((e) => {
    let name = cleanName(e.name);
    if (used.has(name)) {
      const dot = name.lastIndexOf('.');
      const base = dot > 0 ? name.slice(0, dot) : name;
      const ext = dot > 0 ? name.slice(dot) : '';
      let n = 1;
      while (used.has(`${base} (${n})${ext}`)) n++;
      name = `${base} (${n})${ext}`;
    }
    used.add(name);
    return { name, stream: e.stream };
  });
}

export function supportsSaveFilePicker(): boolean {
  return typeof (window as any).showSaveFilePicker === 'function';
}

/**
 * Save `entries` as one ZIP. On Chromium it opens the native Save dialog (the OS dialog
 * is the write consent — no "edit files" prompt) and streams straight to disk. Elsewhere
 * it builds the archive in memory and downloads it. Returns 'cancelled' if the user
 * dismisses the Save dialog. `suggestedName` should end in `.zip`.
 */
export async function saveZip(suggestedName: string, entries: ZipEntry[], onProgress?: ZipProgress): Promise<'saved' | 'cancelled'> {
  const list = dedupe(entries);
  if (!list.length) return 'cancelled';
  if (supportsSaveFilePicker()) {
    let handle: any;
    try {
      handle = await (window as any).showSaveFilePicker({
        suggestedName,
        types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
      });
    } catch {
      return 'cancelled'; // user dismissed the Save dialog
    }
    const writable = await handle.createWritable();
    await writeZip(writable as unknown as WritableStream<Uint8Array>, list, onProgress);
    return 'saved';
  }
  // Fallback (Firefox/Safari): assemble in memory, then download via <a>. Higher memory use.
  const chunks: Uint8Array[] = [];
  const sink = new WritableStream<Uint8Array>({ write(c) { chunks.push(c); } });
  await writeZip(sink, list, onProgress);
  const blob = new Blob(chunks as unknown as BlobPart[], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = suggestedName; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return 'saved';
}
